import { useMutation, useQueryClient } from '@tanstack/react-query';
import { applyToJob } from '@worker/services/MyJobs/api';
import {
  WorkerJobCommonResultsResponse,
  JobQuestionResponse,
} from '@worker/types/myJobs.type';
import useNotification from '@hooks/useNotification';
import {
  extractErrorMessage,
  updateJobApplicationStatus,
} from '@worker/helpers/my-jobs-helpers';
import QueryKeys from '@constants/queryKeys';

interface ApplyToJobParams {
  jobId: number;
  jobName?: string;
  note?: string;
  responses?: JobQuestionResponse[];
  files?: Map<number, File>;
}

/**
 * Hook to apply to a job
 * Updates cache on success only - no optimistic updates or query invalidation
 */
export function useApplyToJobMutation() {
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  return useMutation({
    mutationFn: ({ jobId, note, responses, files }: ApplyToJobParams) =>
      applyToJob(jobId, note, responses, files),
    onSuccess: (response, { jobId, jobName }) => {
      const message = jobName
        ? `You have successfully applied to ${jobName}!`
        : 'You have successfully applied to this job!';
      showSuccessNotification(message);

      // Backend returns complete jobApplication and jobAssignment when applied successfully
      const applicationData = response.jobApplication || undefined;
      const assignmentData = response.jobAssignment || undefined;

      // Update ALL job recommendation caches that contain this job
      // Include jobAssignment to sync chat availability (status becomes WORKER_APPLIED)
      queryClient.setQueriesData<WorkerJobCommonResultsResponse>(
        { queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATIONS] },
        (data) =>
          updateJobApplicationStatus(
            data,
            jobId,
            applicationData,
            assignmentData
          )
      );
    },
    onError: (err: unknown) => {
      showErrorNotification(
        extractErrorMessage(err, 'Failed to apply to job. Please try again.')
      );
    },
  });
}

export default useApplyToJobMutation;
